/**
 * PermanentDeleteDialog — anti-fat-finger confirm dialog per permanent delete.
 *
 * Pattern: l'admin deve TYPARE esattamente il nome dell'entità per abilitare
 * il bottone delete. Stesso pattern di GitHub repo delete / Linear project delete.
 *
 * Use cases: permanent delete company, force-delete user.
 *
 * Reason resta richiesto per audit log.
 */
import { useEffect, useState } from 'react';
import { Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface PermanentDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Tipo di entità — visualizzato nel titolo (es. "company", "user"). */
  entityKind: string;
  /** Nome esatto da typare per abilitare delete (es. "doona.com" o "alessandro@filotrack.com"). */
  expectedName: string;
  onConfirm: (reason: string) => Promise<void>;
}

export function PermanentDeleteDialog({
  open,
  onOpenChange,
  entityKind,
  expectedName,
  onConfirm,
}: PermanentDeleteDialogProps) {
  const [typedName, setTypedName] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setTypedName('');
      setReason('');
    }
  }, [open]);

  const nameMatches = typedName.trim() === expectedName;
  const canSubmit = nameMatches && reason.trim().length > 0 && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await onConfirm(reason.trim());
      onOpenChange(false);
    } catch (err) {
      toast.error((err as Error).message ?? 'Permanent delete failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="h-4 w-4" />
            Permanently delete {entityKind}
          </AlertDialogTitle>
          <AlertDialogDescription>
            This action is <strong>irreversible</strong>. All data associated with{' '}
            <code className="px-1 py-0.5 rounded bg-muted text-foreground text-xs">{expectedName}</code>{' '}
            will be removed. Backups may still hold historical copies but the live state cannot be restored automatically.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="confirm-name">
              Type <code className="px-1 py-0.5 rounded bg-muted text-foreground text-xs">{expectedName}</code> to confirm
            </Label>
            <Input
              id="confirm-name"
              placeholder={expectedName}
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              disabled={busy}
              autoFocus
              autoComplete="off"
              spellCheck={false}
            />
            {typedName && !nameMatches && (
              <p className="text-xs text-destructive">Name does not match.</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="reason-perm">Reason (required)</Label>
            <Input
              id="reason-perm"
              placeholder="Brief reason for audit log..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={busy}
            />
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <Button variant="destructive" onClick={submit} disabled={!canSubmit}>
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            Delete permanently
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
